import { config } from "../config";
import axios from "axios";

export interface Client {
  id: string;
  name: string;
  phone: string;
  email: string;
}

export interface Appointment {
  id: string;
  business_id: string;
  client_id: string;
  employee_id?: string;
  service_id: string;
  appointment_date: string;
  duration_minutes?: number;
  status: string;
  total_price?: number;
  notes?: string;
  payment_status?: string;
  payment_method?: string;
  created_at: string;
  updated_at: string;
  client?: Client;
}

export interface CreateAppointmentData {
  business_id: string;
  service_id: string;
  employee_id: string;
  appointment_date: string; // Format: "DD.MM.YYYY"
  duration_minutes?: number;
  status?: string;
  total_price?: number;
  notes?: string;
  payment_status?: string;
  payment_method?: string;
}

export interface AppointmentResponse {
  message: string;
  appointment: Appointment;
}

const API_BASE_URL = config.api.baseUrl;

/**
 * Get authentication token from localStorage
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Create a new appointment
 */
export const createAppointment = async (
  appointmentData: CreateAppointmentData
): Promise<Appointment> => {
  try {
    const response = await axios.post<AppointmentResponse>(
      `${API_BASE_URL}/appointments`,
      appointmentData,
      {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      }
    );

    return response.data.appointment;
  } catch (error: any) {
    console.error("Error creating appointment:", error);
    throw new Error(
      error.response?.data?.details ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create appointment"
    );
  }
};

/**
 * Get all appointments (admin only)
 */
export const getAllAppointments = async (): Promise<Appointment[]> => {
  try {
    const response = await axios.get<{
      appointments: Appointment[];
      total: number;
    }>(`${API_BASE_URL}/appointments`, {
      headers: getAuthHeaders(),
    });

    return response.data.appointments;
  } catch (error: any) {
    console.error("Error getting appointments:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to get appointments"
    );
  }
};

/**
 * Get a specific appointment by ID
 */
export const getAppointmentById = async (id: string): Promise<Appointment> => {
  try {
    const response = await axios.get<Appointment>(
      `${API_BASE_URL}/appointments/${id}`,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Error getting appointment:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to get appointment"
    );
  }
};

/**
 * Update an appointment
 */
export const updateAppointment = async (
  id: string,
  appointmentData: Partial<CreateAppointmentData>
): Promise<Appointment> => {
  try {
    const response = await axios.put<Appointment>(
      `${API_BASE_URL}/appointments/${id}`,
      appointmentData,
      {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Error updating appointment:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to update appointment"
    );
  }
};

/**
 * Delete an appointment
 */
export const deleteAppointment = async (id: string): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/appointments/${id}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    console.error("Error deleting appointment:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to delete appointment"
    );
  }
};

/**
 * Get appointments by business (for business owners)
 */
export const getAppointmentsByBusiness = async (
  businessId: string
): Promise<Appointment[]> => {
  try {
    const response = await axios.get<{
      appointments: Appointment[];
      total: number;
    }>(`${API_BASE_URL}/appointments?business_id=${businessId}`, {
      headers: getAuthHeaders(),
    });

    return response.data.appointments;
  } catch (error: any) {
    console.error("Error getting business appointments:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to get business appointments"
    );
  }
};

/**
 * Get completed appointments for a business
 */
export const getCompletedAppointments = async (
  businessId: string
): Promise<Appointment[]> => {
  try {
    const response = await axios.get<{
      appointments: Appointment[];
      total: number;
    }>(`${API_BASE_URL}/appointments/completed?business_id=${businessId}`, {
      headers: getAuthHeaders(),
    });

    return response.data.appointments;
  } catch (error: any) {
    console.error("Error getting completed appointments:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to get completed appointments"
    );
  }
};

/**
 * Get upcoming appointments for a business
 */
export const getUpcomingAppointments = async (
  businessId: string
): Promise<Appointment[]> => {
  try {
    const response = await axios.get<{
      appointments: Appointment[];
      total: number;
    }>(`${API_BASE_URL}/appointments/upcoming?business_id=${businessId}`, {
      headers: getAuthHeaders(),
    });

    return response.data.appointments;
  } catch (error: any) {
    console.error("Error getting upcoming appointments:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to get upcoming appointments"
    );
  }
};

/**
 * Update past appointments to completed status
 */
export const updatePastAppointments = async (): Promise<void> => {
  try {
    await axios.post(`${API_BASE_URL}/appointments/update-past`, {}, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    console.error("Error updating past appointments:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to update past appointments"
    );
  }
};
